import React, { useState, useEffect } from "react";
import {
  UserGroupIcon,
  ShieldCheckIcon,
  StarIcon,
  UserMinusIcon,
} from "@heroicons/react/24/outline";
import OrganizationChart from "./OrganizationChart";

const TeamCollabrationDashboard = ({
  dashboardData,
  companyEmployeesForChart = [],
  isOdooConfigured = false,
  hrmsSource,
}) => {
  const [primaryColor, setPrimaryColor] = useState("blue");

  // Read primaryColor from app settings
  useEffect(() => {
    const savedSettings = localStorage.getItem("platformSettings");
    if (savedSettings) {
      try {
        const settings = JSON.parse(savedSettings);
        const color = settings.appearance?.primaryColor || "#3b82f6";

        // Map hex colors to our color names
        const colorMap = {
          "#3b82f6": "blue",
          "#22c55e": "green",
          "#9333ea": "purple",
          "#06b6d4": "cyan",
          "#ec4853": "pink", // Note: user might use different pink hex
          "#ea b308": "yellow",
          "#6366f1": "indigo",
          "#ef4444": "red",
        };

        const colorName = colorMap[color] || "blue";
        setPrimaryColor(colorName);
      } catch (error) {
        console.error("Error reading settings:", error);
      }
    }
  }, []);
  return (
    <div className="space-y-6">
      {/* Dashboard Overview Cards */}
      {dashboardData && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="relative overflow-hidden bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl shadow-md border border-blue-200 p-6 hover:shadow-lg transition-all duration-300 group">
            <div className="absolute inset-0 opacity-5 group-hover:opacity-10 transition-opacity">
              <div className="absolute top-0 right-0 w-20 h-20 bg-blue-400 rounded-full -mr-10 -mt-10"></div>
            </div>
            <div className="relative z-10">
              <div className="flex items-start justify-between mb-3">
                <div className="p-2.5 bg-blue-600 rounded-lg shadow-md">
                  <UserGroupIcon className="w-6 h-6 text-white" />
                </div>
              </div>
              <p className="text-sm font-medium text-blue-600 uppercase tracking-wide mb-1">
                Total Employees
              </p>
              <p className="text-3xl font-bold text-blue-900">
                {dashboardData.total_employees || 0}
              </p>
            </div>
          </div>

          <div className="relative overflow-hidden bg-gradient-to-br from-green-50 to-green-100 rounded-xl shadow-md border border-green-200 p-6 hover:shadow-lg transition-all duration-300 group">
            <div className="absolute inset-0 opacity-5 group-hover:opacity-10 transition-opacity">
              <div className="absolute top-0 right-0 w-20 h-20 bg-green-400 rounded-full -mr-10 -mt-10"></div>
            </div>
            <div className="relative z-10">
              <div className="flex items-start justify-between mb-3">
                <div className="p-2.5 bg-green-600 rounded-lg shadow-md">
                  <ShieldCheckIcon className="w-6 h-6 text-white" />
                </div>
              </div>
              <p className="text-sm font-medium text-green-600 uppercase tracking-wide mb-1">
                BGV Verified
              </p>
              <p className="text-3xl font-bold text-green-900">
                {dashboardData.bgv_summary?.verified || 0}
              </p>
            </div>
          </div>

          <div className="relative overflow-hidden bg-gradient-to-br from-amber-50 to-amber-100 rounded-xl shadow-md border border-amber-200 p-6 hover:shadow-lg transition-all duration-300 group">
            <div className="absolute inset-0 opacity-5 group-hover:opacity-10 transition-opacity">
              <div className="absolute top-0 right-0 w-20 h-20 bg-amber-400 rounded-full -mr-10 -mt-10"></div>
            </div>
            <div className="relative z-10">
              <div className="flex items-start justify-between mb-3">
                <div className="p-2.5 bg-amber-600 rounded-lg shadow-md">
                  <StarIcon className="w-6 h-6 text-white" />
                </div>
              </div>
              <p className="text-sm font-medium text-amber-600 uppercase tracking-wide mb-1">
                Excellent Performance
              </p>
              <p className="text-3xl font-bold text-amber-900">
                {dashboardData.performance_summary?.excellent_rating || 0}
              </p>
            </div>
          </div>

          <div className="relative overflow-hidden bg-gradient-to-br from-red-50 to-red-100 rounded-xl shadow-md border border-red-200 p-6 hover:shadow-lg transition-all duration-300 group">
            <div className="absolute inset-0 opacity-5 group-hover:opacity-10 transition-opacity">
              <div className="absolute top-0 right-0 w-20 h-20 bg-red-400 rounded-full -mr-10 -mt-10"></div>
            </div>
            <div className="relative z-10">
              <div className="flex items-start justify-between mb-3">
                <div className="p-2.5 bg-red-600 rounded-lg shadow-md">
                  <UserMinusIcon className="w-6 h-6 text-white" />
                </div>
              </div>
              <p className="text-sm font-medium text-red-600 uppercase tracking-wide mb-1">
                Relieved Employees
              </p>
              <p className="text-3xl font-bold text-red-900">
                {dashboardData.relieved_summary?.total_relieved || 0}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Summary Cards */}
      {dashboardData && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-6 hover:shadow-xl transition-all">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-bold text-gray-900">
                BGV Verification Status
              </h3>
              <div className="p-2 bg-green-100 rounded-lg">
                <ShieldCheckIcon className="w-5 h-5 text-green-600" />
              </div>
            </div>
            <div className="space-y-4">
              <div className="bg-gradient-to-r from-green-50 to-transparent p-4 rounded-lg border border-green-100">
                <div className="flex justify-between items-end mb-2">
                  <span className="text-sm font-medium text-gray-700">
                    Verified
                  </span>
                  <span className="text-2xl font-bold text-green-600">
                    {dashboardData.bgv_summary?.verified || 0}
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2.5">
                  <div
                    className="bg-gradient-to-r from-green-500 to-green-600 h-2.5 rounded-full transition-all"
                    style={{
                      width: `${dashboardData.bgv_summary?.verification_rate || 0}%`,
                    }}
                  ></div>
                </div>
              </div>
              <div className="bg-gradient-to-r from-yellow-50 to-transparent p-4 rounded-lg border border-yellow-100">
                <div className="flex justify-between items-end">
                  <span className="text-sm font-medium text-gray-700">
                    Pending
                  </span>
                  <span className="text-lg font-bold text-yellow-600">
                    {dashboardData.bgv_summary?.pending || 0}
                  </span>
                </div>
              </div>
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200 flex justify-between items-center">
                <span className="font-medium text-blue-900">
                  Verification Rate
                </span>
                <span className="text-2xl font-bold text-blue-600">
                  {dashboardData.bgv_summary?.verification_rate || 0}%
                </span>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-6 hover:shadow-xl transition-all">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-bold text-gray-900">
                Performance Overview
              </h3>
              <div className="p-2 bg-amber-100 rounded-lg">
                <StarIcon className="w-5 h-5 text-amber-600" />
              </div>
            </div>
            <div className="space-y-4">
              <div className="bg-gradient-to-r from-amber-50 to-transparent p-4 rounded-lg border border-amber-100">
                <div className="flex justify-between items-end mb-2">
                  <span className="text-sm font-medium text-gray-700">
                    Excellent Rating
                  </span>
                  <span className="text-2xl font-bold text-amber-600">
                    {dashboardData.performance_summary?.excellent_rating || 0}
                  </span>
                </div>
                <div className="flex gap-1 mt-3">
                  {[...Array(5)].map((_, i) => (
                    <div
                      key={i}
                      className={`h-2 flex-1 rounded-full ${i <
                          Math.round(
                            (dashboardData.performance_summary
                              ?.excellent_rating || 0) / 20,
                          )
                          ? "bg-gradient-to-r from-amber-400 to-amber-500"
                          : "bg-gray-200"
                        }`}
                    ></div>
                  ))}
                </div>
              </div>
              <div className="bg-gradient-to-r from-purple-50 to-transparent p-4 rounded-lg border border-purple-100">
                <div className="flex justify-between items-end">
                  <span className="text-sm font-medium text-gray-700">
                    Avg KPI Score
                  </span>
                  <span className="text-2xl font-bold text-purple-600">
                    {dashboardData.performance_summary?.average_kpi || 0}
                  </span>
                </div>
              </div>
              <div className="bg-indigo-50 p-4 rounded-lg border border-indigo-200 flex justify-between items-center">
                <span className="font-medium text-indigo-900">
                  Excellence Rate
                </span>
                <span className="text-2xl font-bold text-indigo-600">
                  {dashboardData.performance_summary?.excellence_rate || 0}%
                </span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Organization Chart */}
      <OrganizationChart
        employees={companyEmployeesForChart}
        primaryColor={primaryColor}
        isOdooConfigured={isOdooConfigured}
        hrmsSource={hrmsSource}
      />
    </div>
  );
};

export default TeamCollabrationDashboard;
